import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Thermometer,
  Gauge,
  Wind,
  Box,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ChevronRight,
  AlertTriangle,
  Star,
  FlaskConical,
  Printer,
  Sun
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const difficultyColors = {
  beginner: 'bg-green-600',
  intermediate: 'bg-yellow-600',
  advanced: 'bg-red-600',
  specialty: 'bg-purple-600'
};

const PRINTER_TYPES = [
  { id: 'all', label: 'All Materials', icon: Box },
  { id: 'fdm', label: 'FDM Filaments', icon: Printer },
  { id: 'resin', label: 'Resin / MSLA', icon: FlaskConical },
];

const MaterialsGuide = () => {
  const [materials, setMaterials] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printerTypeFilter, setPrinterTypeFilter] = useState('all');

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const response = await axios.get(`${API}/printing3d/materials`);
        setMaterials(response.data);
      } catch (error) {
        console.error('Failed to fetch materials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, []);

  const filteredMaterials = useMemo(() => {
    if (printerTypeFilter === 'all') return materials;
    return Object.fromEntries(
      Object.entries(materials).filter(([, mat]) =>
        (mat.printer_type || 'fdm') === printerTypeFilter
      )
    );
  }, [materials, printerTypeFilter]);

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.PRINTING3D_MATERIALS)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Box className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading materials guide...</p>
        </div>
      </div>
    );
  }

  // Material Detail View
  if (selectedMaterial) {
    const mat = materials[selectedMaterial];
    
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="material-detail" style={bgStyle(IMAGES.PRINTING3D_MATERIALS)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        {/* Back Button */}
        <button 
          onClick={() => setSelectedMaterial(null)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Materials
        </button>

        <div className="max-w-4xl mx-auto">
          {/* Material Header */}
          <div className="p-6 rounded-xl bg-gradient-to-br from-orange-500/50 to-orange-600/30 border border-orange-500/30 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <Badge className={`${difficultyColors[mat.difficulty]} mb-2`}>
                  {mat.difficulty.charAt(0).toUpperCase() + mat.difficulty.slice(1)}
                </Badge>
                <h1 className="text-3xl font-bold text-white mb-1">{mat.name}</h1>
                <p className="text-xl text-zinc-400">{mat.full_name}</p>
              </div>
              {mat.enclosure_required && (
                <Badge className="bg-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Enclosure Required
                </Badge>
              )}
            </div>
            <p className="mt-4 text-zinc-300">{mat.description}</p>
          </div>

          {/* Resin Safety Banner */}
          {mat.printer_type === 'resin' && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-900/40 border border-red-500/50 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-semibold text-sm mb-1">Resin Safety Warning</p>
                <p className="text-red-200 text-sm">{mat.safety_warning || 'Uncured resin is toxic. Wear nitrile gloves and eye protection. Work in ventilated area. Cure all liquid waste before disposal.'}</p>
              </div>
            </div>
          )}

          {/* FDM: Temperature Settings */}
          {mat.printer_type !== 'resin' && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Thermometer className="w-5 h-5 text-red-400" />
                  Temperature Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <div className="text-zinc-400 text-sm mb-1">Nozzle Temperature</div>
                    <div className="text-2xl font-bold text-white">
                      {mat.nozzle_temp_min}°C – {mat.nozzle_temp_max}°C
                    </div>
                    <div className="text-sm text-orange-400 mt-1">
                      Recommended: {mat.nozzle_temp_default}°C
                    </div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg">
                    <div className="text-zinc-400 text-sm mb-1">Bed Temperature</div>
                    <div className="text-2xl font-bold text-white">
                      {mat.bed_temp_min}°C – {mat.bed_temp_max}°C
                    </div>
                    <div className="text-sm text-orange-400 mt-1">
                      Recommended: {mat.bed_temp_default}°C
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resin: Exposure Settings */}
          {mat.printer_type === 'resin' && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sun className="w-5 h-5 text-yellow-400" />
                  Exposure Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">UV Wavelength</div>
                    <div className="text-xl font-bold text-white">{mat.uv_wavelength || 405}nm</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Layer Thickness</div>
                    <div className="text-xl font-bold text-white">
                      {mat.layer_thickness_min_mm}–{mat.layer_thickness_max_mm}mm
                    </div>
                    <div className="text-xs text-orange-400 mt-1">Recommended: {mat.layer_thickness_default_mm}mm</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Normal Exposure</div>
                    <div className="text-xl font-bold text-white">{mat.exposure_time_default_s}s</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Bottom Exposure</div>
                    <div className="text-xl font-bold text-white">{mat.bottom_exposure_time_s}s</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Bottom Layers</div>
                    <div className="text-xl font-bold text-white">{mat.bottom_layers}</div>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Lift Speed</div>
                    <div className="text-xl font-bold text-white">{mat.lift_speed_mm_min} mm/min</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* FDM: Print Settings */}
          {mat.printer_type !== 'resin' && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-blue-400" />
                  Print Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Print Speed</div>
                    <div className="text-lg font-bold text-white">
                      {mat.print_speed_min}–{mat.print_speed_max}
                    </div>
                    <div className="text-xs text-zinc-500">mm/s</div>
                  </div>
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Retraction</div>
                    <div className="text-lg font-bold text-white">
                      {mat.retraction_distance}mm
                    </div>
                    <div className="text-xs text-zinc-500">Bowden setup</div>
                  </div>
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Retraction (DD)</div>
                    <div className="text-lg font-bold text-white">
                      {mat.retraction_distance_direct}mm
                    </div>
                    <div className="text-xs text-zinc-500">Direct Drive</div>
                  </div>
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-center">
                    <div className="text-zinc-400 text-xs mb-1">Cooling Fan</div>
                    <div className="text-lg font-bold text-white">
                      {mat.cooling_fan}%
                    </div>
                    <div className="text-xs text-zinc-500">Part cooling</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resin: Post Processing & Storage */}
          {mat.printer_type === 'resin' && (mat.post_processing || mat.storage) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {mat.post_processing && (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Sun className="w-4 h-4 text-yellow-400" />
                      Post Processing
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-300">{mat.post_processing}</p>
                  </CardContent>
                </Card>
              )}
              {mat.storage && (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Box className="w-4 h-4 text-blue-400" />
                      Storage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-300">{mat.storage}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Best For / Avoid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Card className="bg-green-900/20 border-green-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Best For
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {mat.best_for?.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-green-300">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  Avoid For
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {mat.avoid_for?.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-red-300">
                      <XCircle className="w-4 h-4 text-red-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Materials Grid View
  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="materials-guide" style={bgStyle(IMAGES.PRINTING3D_MATERIALS)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Box className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">MATERIALS LIBRARY</div>
            <h1
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Material Guide
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">FDM filaments, resins, and material properties</p>
          </div>
        </div>
      </div>

      {/* Resin safety notice */}
      {(printerTypeFilter === 'resin' || printerTypeFilter === 'all') && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-900/30 border border-red-500/40 mb-4">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-xs"><span className="font-semibold">Resin Safety:</span> Uncured resin is toxic. Always wear nitrile gloves and eye protection. Work in ventilated area. Cure all liquid resin waste before disposal.</p>
        </div>
      )}

      {/* Printer type filter tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {PRINTER_TYPES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setPrinterTypeFilter(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              printerTypeFilter === id
                ? 'bg-orange-500 text-black'
                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Materials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(filteredMaterials).map(([key, mat]) => {
          const isResin = mat.printer_type === 'resin';
          return (
            <Card
              key={key}
              className={`bg-zinc-900 border-zinc-800 hover:border-orange-500 transition-all cursor-pointer ${isResin ? 'border-cyan-800/50' : ''}`}
              onClick={() => setSelectedMaterial(key)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isResin && <FlaskConical className="w-4 h-4 text-cyan-400" />}
                    <CardTitle className="text-xl text-white">{mat.name}</CardTitle>
                  </div>
                  <Badge className={difficultyColors[mat.difficulty] || 'bg-zinc-600'}>
                    {mat.difficulty}
                  </Badge>
                </div>
                <CardDescription className="text-zinc-400">{mat.full_name}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{mat.description}</p>

                {/* Quick Stats — FDM */}
                {!isResin && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <div className="text-xs text-zinc-500">Nozzle</div>
                      <div className="text-sm text-white font-medium">{mat.nozzle_temp_default}°C</div>
                    </div>
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <div className="text-xs text-zinc-500">Bed</div>
                      <div className="text-sm text-white font-medium">{mat.bed_temp_default}°C</div>
                    </div>
                  </div>
                )}

                {/* Quick Stats — Resin */}
                {isResin && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <div className="text-xs text-zinc-500">Exposure</div>
                      <div className="text-sm text-white font-medium">{mat.exposure_time_default_s}s</div>
                    </div>
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <div className="text-xs text-zinc-500">Layer</div>
                      <div className="text-sm text-white font-medium">{mat.layer_thickness_default_mm}mm</div>
                    </div>
                  </div>
                )}

                {mat.enclosure_required && (
                  <Badge variant="outline" className="border-red-500 text-red-400 mb-3">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Enclosure Required
                  </Badge>
                )}

                {isResin && (
                  <Badge variant="outline" className="border-red-500 text-red-400 mb-3">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    PPE Required
                  </Badge>
                )}

                <div className="flex items-center gap-1 text-orange-400 text-sm">
                  <span>View details</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MaterialsGuide;
